/* eslint-disable react-hooks/rules-of-hooks */
import { useTranslation } from 'react-i18next';
import { formatter } from '@repo/shared';
import { type ColumnDef } from '@tanstack/react-table';

import { TextTranslationModelRun } from '@/api/generated.schemas';
import { ColumnHeader } from '@/components/blocks/data-table/column-header';
import { Preview } from '@/components/blocks/preview/preview';

export const columns: Array<ColumnDef<TextTranslationModelRun>> = [
  {
    accessorKey: 'input',
    header: ({ column }) => {
      const { t } = useTranslation();
      return (
        <ColumnHeader
          column={column}
          title={t('overview.input')}
        />
      );
    },
    cell: ({ row }) => {
      const input = row.original.input;
      if (!input?.path) {
        return null;
      }
      return (
        <Preview
          id={input.id}
          options={{
            mutationKey: `${input.id}`,
            path: input.path,
          }}
        />
      );
    },
  },
  {
    accessorKey: 'reference',
    header: ({ column }) => {
      const { t } = useTranslation();
      return (
        <ColumnHeader
          column={column}
          title={t('overview.reference')}
        />
      );
    },
    cell: ({ row }) => {
      const reference = row.original.reference;
      if (!reference?.path) {
        return null;
      }
      return (
        <Preview
          id={reference.id}
          options={{
            mutationKey: `${reference.id}`,
            path: reference.path,
          }}
        />
      );
    },
  },
  {
    accessorKey: 'sourceLanguageCode',
    header: ({ column }) => {
      const { t } = useTranslation();
      return (
        <ColumnHeader
          column={column}
          title={t('overview.sourceLanguageCode')}
        />
      );
    },
    cell: ({ row }) => row.original.sourceLanguageCode,
  },
  {
    accessorKey: 'targetLanguageCode',
    header: ({ column }) => {
      const { t } = useTranslation();
      return (
        <ColumnHeader
          column={column}
          title={t('overview.targetLanguageCode')}
        />
      );
    },
    cell: ({ row }) => row.original.targetLanguageCode,
  },
  {
    id: 'bleu',
    header: ({ column }) => {
      const { t } = useTranslation();
      return (
        <ColumnHeader
          column={column}
          title={t('metrics.bleu')}
        />
      );
    },
    cell: ({ row }) => formatter.metrics.bleu(row.original.metrics?.bleu),
  },
  {
    id: 'comet',
    header: ({ column }) => {
      const { t } = useTranslation();
      return (
        <ColumnHeader
          column={column}
          title={t('metrics.comet')}
        />
      );
    },
    cell: ({ row }) => formatter.metrics.comet(row.original.metrics?.comet),
  },
  {
    accessorKey: 'output',
    header: ({ column }) => {
      const { t } = useTranslation();
      return (
        <ColumnHeader
          column={column}
          title={t('overview.output')}
        />
      );
    },
    cell: ({ row }) => {
      const { output, error } = row.original;
      if (error) {
        return (
          <span className='text-destructive-foreground'>
            {error.description}
          </span>
        );
      }
      if (output?.path)
        return (
          <Preview
            id={output.id}
            options={{
              mutationKey: `${output.id}`,
              path: output.path,
            }}
          />
        );

      return null;
    },
  },
];
